import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../hooks/useToast';
import LoadingSpinner from './LoadingSpinner';
import { trackEvent } from '../../utils/analytics';

const ProtectedRoute = ({ children, roles }) => {
  const { user, loading, isAuthenticated } = useAuth();
  const { showToast } = useToast();
  const location = useLocation();

  useEffect(() => {
    // Track authentication state for analytics
    if (!loading) {
      if (!isAuthenticated) {
        trackEvent('auth_redirect', { 
          reason: 'not_authenticated',
          attempted_path: window.location.pathname
        });
      } else if (roles && (!user || !roles.includes(user.role))) {
        trackEvent('auth_redirect', { 
          reason: 'insufficient_permissions',
          user_role: user?.role || 'unknown',
          required_roles: roles,
          attempted_path: window.location.pathname
        });
      }
    }
  }, [loading, isAuthenticated, user, roles]);

  if (loading) {
    return (
      <div className="h-screen flex justify-center items-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!isAuthenticated) {
    // Redirect to login with a return path
    return (
      <Navigate 
        to="/login" 
        state={{ 
          returnUrl: location.pathname,
          message: 'Please log in to access this page' 
        }} 
        replace 
      />
    );
  }

  if (roles && (!user || !roles.includes(user.role))) {
    // Show a toast message about insufficient permissions
    showToast(`You don't have permission to access this page. Required role: ${roles.join(' or ')}`, 'error');
    
    // Redirect to the appropriate dashboard based on user role
    let redirectPath = '/';
    
    if (user) {
      switch (user.role) {
        case 'system_admin':
          redirectPath = '/admin';
          break;
        case 'school_admin':
          redirectPath = '/school';
          break;
        case 'teacher':
          redirectPath = '/teacher';
          break;
        case 'parent':
          redirectPath = '/parent';
          break;
        case 'mentor':
          redirectPath = '/mentor';
          break;
        default:
          redirectPath = '/';
      }
    }
    
    return <Navigate to={redirectPath} replace />;
  }

  return children;
};

export default ProtectedRoute;